import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { TrendingUp, Sparkles, Award, Rocket, IndianRupee, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PartnerEarning — public landing page.
 * Route: /partner-earning  (also linked from public Navbar)
 * — 3 real earning stories with animated counters
 * — Live calculator
 * — 4-step how-it-works + ₹999 CTA
 */
export default function PartnerEarning() {
  const [stories, setStories] = useState([]);
  const [svc, setSvc] = useState("luggage");
  const [hours, setHours] = useState(6);
  const [days, setDays] = useState(6);
  const [calc, setCalc] = useState({ month_low_inr: 0, month_high_inr: 0, monthly_median_inr: 0 });

  useEffect(() => {
    axios.get(`${API}/partner/earning/stories`).then((r) => setStories(r.data.stories || []));
  }, []);
  useEffect(() => {
    axios.get(`${API}/partner/earning/calculator`, { params: { service: svc, hours, days } })
         .then((r) => setCalc(r.data));
  }, [svc, hours, days]);

  return (
    <div className="pb-24" data-testid="partner-earning-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: "radial-gradient(1200px 400px at 20% 0%, #F4511822 0%, transparent 60%), radial-gradient(900px 400px at 90% 20%, #00604A22 0%, transparent 60%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-10">
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow"
               style={{ background: "linear-gradient(90deg,#F45118,#DC2626)" }}>
            <Sparkles className="w-3 h-3" /> HeylpMate Partner Program
          </div>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl font-bold leading-[0.95] tracking-tight" style={{ color: "#00604A" }}>
            Real people.<br />
            <span className="italic" style={{ color: "#F45118" }}>Real earnings.</span>
          </h1>
          <p className="mt-4 text-lg max-w-2xl" style={{ color: "#667085" }}>
            Start your HeylpMate income today. Fixed routes, flexible hours, and 25% of every transaction to your UPI — every month.
          </p>
        </div>
      </section>

      {/* EARNING STORY CARDS */}
      <section className="max-w-6xl mx-auto px-5 md:px-8" data-testid="earning-stories">
        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {stories.map((s) => <EarningStoryCard key={s.slug} s={s} />)}
        </div>
        <p className="mt-4 text-xs text-center max-w-2xl mx-auto" style={{ color: "#667085" }}>
          These are real HeylpMate partners. <b>Your earning depends on your effort and city.</b>
        </p>
      </section>

      {/* EARNING CALCULATOR */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mt-14" data-testid="earning-calculator">
        <div className="grid md:grid-cols-5 gap-4 items-stretch">
          <div className="md:col-span-3 rounded-3xl border p-6 md:p-7 bg-white" style={{ borderColor: "#E4E7E5" }}>
            <span className="label-eyebrow">Earning calculator</span>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold" style={{ color: "#00604A" }}>
              How much can <span style={{ color: "#F45118" }}>you</span> earn?
            </h2>
            <div className="mt-6 space-y-5">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Service</span>
                <select data-testid="calc-service" value={svc} onChange={(e) => setSvc(e.target.value)}
                        className="mt-1 w-full border-2 rounded-2xl px-3 py-2.5 outline-none text-sm font-bold"
                        style={{ borderColor: "#E4E7E5", color: "#00604A" }}>
                  <option value="luggage">Rail Luggage Assist</option>
                  <option value="auto">Auto Drop & Pick</option>
                  <option value="campus">Campus Sessions</option>
                  <option value="flatmate">Flatmate Verifier</option>
                  <option value="namecraft">NameCraft Sessions</option>
                  <option value="general">General Assist</option>
                </select>
              </label>
              <label className="block">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Hours per day</span>
                  <span className="text-sm font-bold" style={{ color: "#00604A" }}>{hours}h</span>
                </div>
                <input data-testid="calc-hours" type="range" min={2} max={10} value={hours}
                       onChange={(e) => setHours(Number(e.target.value))} className="w-full mt-1 accent-[#F45118]" />
              </label>
              <label className="block">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Days per week</span>
                  <span className="text-sm font-bold" style={{ color: "#00604A" }}>{days}d</span>
                </div>
                <input data-testid="calc-days" type="range" min={1} max={7} value={days}
                       onChange={(e) => setDays(Number(e.target.value))} className="w-full mt-1 accent-[#F45118]" />
              </label>
            </div>
          </div>

          <div className="md:col-span-2 rounded-3xl p-6 md:p-7 text-white shadow-lg"
               style={{ background: "linear-gradient(160deg,#00604A 0%,#0B7A5A 60%,#F45118 130%)" }}>
            <div className="text-xs uppercase tracking-widest opacity-90">Est. monthly earning</div>
            <div className="mt-3 flex items-baseline gap-1">
              <IndianRupee className="w-8 h-8 opacity-80" />
              <AnimatedCounter value={calc.monthly_median_inr} className="font-display text-5xl md:text-6xl font-bold" />
            </div>
            <div className="mt-1 text-xs opacity-90">Range · ₹{calc.month_low_inr?.toLocaleString?.() || calc.month_low_inr} – ₹{calc.month_high_inr?.toLocaleString?.() || calc.month_high_inr}</div>
            <div className="mt-6 pt-6 border-t border-white/20 text-xs opacity-90">
              25% of every HeylpMate transaction paid to your UPI · monthly.
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS + ₹999 CTA */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mt-14" data-testid="how-it-works">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-center" style={{ color: "#00604A" }}>
          Start earning in <span style={{ color: "#F45118" }}>4 steps</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Join as Partner", d: "₹999 one-time · KYC in 5 min" },
            { n: "02", t: "Choose your service", d: "Pick one or many — luggage, auto, campus…" },
            { n: "03", t: "Get work from HeylpMate", d: "We route real customers to your UPI-linked profile." },
            { n: "04", t: "Earn every month", d: "25% of every transaction, paid directly to your UPI." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border p-5 bg-white card-lift"
                 style={{ borderColor: "#00604A22" }}>
              <div className="text-4xl font-display font-bold" style={{ color: "#F45118" }}>{s.n}</div>
              <div className="mt-1 font-display text-lg font-bold" style={{ color: "#00604A" }}>{s.t}</div>
              <div className="text-xs mt-1" style={{ color: "#667085" }}>{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl overflow-hidden border-2 p-6 md:p-8 text-center"
             style={{ borderColor: "#F45118",
                      background: "linear-gradient(135deg,#FFF3EA 0%,#FFE8D5 60%,#FFDDC0 100%)" }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: "#F45118" }}>Ready?</div>
          <h3 className="mt-2 font-display text-3xl md:text-4xl font-bold" style={{ color: "#00604A" }}>
            Start earning today — <span style={{ color: "#F45118" }}>₹999 one-time</span>
          </h3>
          <p className="mt-1 text-sm" style={{ color: "#667085" }}>Verified badge · Access to all services · 25% forever.</p>
          <Link to="/pay-upi?amount=999&purpose=partner_join" data-testid="partner-join-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-white font-bold text-lg shadow-xl hover:scale-[1.02] transition"
                style={{ background: "#F45118" }}>
            <Rocket className="w-5 h-5" /> Start earning — join for ₹999 <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Milestones */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 mt-14" data-testid="partner-milestones">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-center" style={{ color: "#00604A" }}>
          Chase your <span style={{ color: "#F45118" }}>badge</span>
        </h2>
        <p className="text-center text-sm mt-2" style={{ color: "#667085" }}>
          Every earning milestone unlocks a lifetime badge on your partner profile.
        </p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-7 gap-3">
          {[
            ["₹10k", "silver"], ["₹25k", "silver"], ["₹50k", "gold"], ["₹75k", "gold"],
            ["₹1L", "platinum"], ["₹1.25L", "platinum"], ["₹1.5L", "platinum"],
          ].map(([label, tier]) => (
            <div key={label} className="rounded-2xl border p-4 text-center"
                 style={{
                   borderColor: tier === "gold" ? "#F59E0B44" : tier === "platinum" ? "#00604A44" : "#94A3B855",
                   background: tier === "gold" ? "linear-gradient(180deg,#FEF3C7,#FDE68A)"
                             : tier === "platinum" ? "linear-gradient(180deg,#D1FAE5,#A7F3D0)"
                             : "linear-gradient(180deg,#F1F5F9,#E2E8F0)",
                 }}>
              <Award className="w-6 h-6 mx-auto mb-1" style={{
                color: tier === "gold" ? "#B45309" : tier === "platinum" ? "#065F46" : "#475569",
              }} />
              <div className="font-display font-bold" style={{
                color: tier === "gold" ? "#92400E" : tier === "platinum" ? "#065F46" : "#334155",
              }}>{label}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">{tier}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EarningStoryCard({ s }) {
  return (
    <div className="rounded-3xl overflow-hidden border card-lift" style={{ borderColor: "#E4E7E5" }} data-testid={`story-${s.slug}`}>
      <div className="h-40 bg-neutral-100 relative overflow-hidden">
        <img src={s.photo} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent 50%,rgba(0,96,74,0.7))" }} />
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <div className="text-[10px] uppercase tracking-widest opacity-95">{s.service}</div>
          <div className="font-display text-lg font-bold">{s.name}</div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-baseline gap-1">
          <IndianRupee className="w-5 h-5" style={{ color: "#F45118" }} />
          <AnimatedCounter value={s.monthly_inr} className="font-display text-3xl font-bold" style={{ color: "#F45118" }} />
          <span className="text-xs font-bold" style={{ color: "#667085" }}>/month</span>
        </div>
        <p className="mt-2 text-xs italic" style={{ color: "#667085" }}>"{s.quote}"</p>
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1"
             style={{ background: "#D1FAE5", color: "#065F46" }}>
          <TrendingUp className="w-3 h-3" /> Real HeylpMate partner
        </div>
      </div>
    </div>
  );
}

function AnimatedCounter({ value = 0, className = "", style }) {
  const [display, setDisplay] = useState(0);
  useMemo(() => {
    let raf = null;
    const start = performance.now();
    const from = 0;
    const to = Number(value) || 0;
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setDisplay(Math.floor(from + (to - from) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className} style={style}>{display.toLocaleString("en-IN")}</span>;
}
